import React, { useRef, useEffect } from 'react';
import { IVideoTrack } from '../../twilioTypes';
import { Track } from 'twilio-video';
import useMediaStreamTrack from '../../hooks/useMediaStreamTrack';

interface VideoTrackProps {
  track: IVideoTrack;
  isLocal?: boolean;
  priority?: Track.Priority | null;
  classNames?: string;
}

export default function VideoTrack({ track, isLocal, priority, classNames }: VideoTrackProps) {
  const ref = useRef<HTMLVideoElement>(null!);
  const mediaStreamTrack = useMediaStreamTrack(track);
  const [videoEnabled, setVideoEnabled] = React.useState<boolean>(true);

  useEffect(() => {
    if (!track.isEnabled)
      setVideoEnabled(false);
    track.on('disabled', () => {
      setVideoEnabled(false);
    });
    track.on('enabled', () => {
      setVideoEnabled(true);
    });

    const el = ref.current;
    el.muted = true;
    if (track.setPriority && priority) {
      track.setPriority(priority);
    }
    track.attach(el);
    return () => {
      track.detach(el);

      // This addresses a Chrome issue where the number of WebMediaPlayers is limited.
      // See: https://github.com/twilio/twilio-video.js/issues/1528
      el.srcObject = null;

      if (track.setPriority && priority) {
        // Passing `null` to setPriority will set the track's priority to that which it was published with.
        track.setPriority(null);
      }
    };
  }, [track, priority]);

  // The local video track is mirrored if it is not facing the environment.
  const isFrontFacing = mediaStreamTrack?.getSettings().facingMode !== 'environment';
  const style = {
    transform: isLocal && isFrontFacing ? 'scaleX(-1)' : '',
    objectFit: 'contain' as const,
  };

  return (<>
    <video className={(!videoEnabled && !isLocal) ? `${classNames} invisible` : classNames} ref={ref} style={style} />
    {!isLocal && <div className={(videoEnabled) ? "d-none" : "video-disabled"}><i className="bi bi-camera-video-off-fill video-room-action-icon"></i></div>}
  </>);
}
